import React, { createContext, useState, useEffect } from 'react';
import { getCurrentUser } from '../api/auth';
import { getAgentPermissions } from '../api/agent';
import { getSubadminPermissions } from '../api/subadmin';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [permissions, setPermissions] = useState(JSON.parse(localStorage.getItem('agentPermissions') || localStorage.getItem('subadminPermissions') || '[]'));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');

    if (token) {
      // If we have stored user data, use it immediately
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (error) {
          console.error('Error parsing stored user data:', error);
        }
      }

      // Still verify with API in background
      getCurrentUser()
        .then(async (response) => {
          console.log('getCurrentUser response:', response);
          const userData = response.agent || response.subadmin || response.ca || response.superadmin;
          console.log('Setting user data:', userData);
          setUser(userData);
          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(userData));

          // Fetch permissions if not superadmin
          if (userData && !userData.issuperadmin) {
            try {
              let perms = [];
              if (userData.issubadmin) {
                perms = await getSubadminPermissions();
                localStorage.setItem('subadminPermissions', JSON.stringify(perms));
              } else if (userData.isagent === 'verified' || userData.isagent === true) {
                perms = await getAgentPermissions(userData.id);
                localStorage.setItem('agentPermissions', JSON.stringify(perms));
              }
              setPermissions(perms);
            } catch (err) {
              console.error('AuthContext fetch permissions error:', err);
            }
          }
        })
        .catch((error) => {
          console.error('Token verification failed:', error);
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      localStorage.removeItem('user');
      setUser(null);
      setLoading(false);
    }
  }, [token]);

  const login = async (userData, tokenValue) => {
    setToken(tokenValue);
    setUser(userData);
    localStorage.setItem('token', tokenValue);
    localStorage.setItem('user', JSON.stringify(userData));

    // Fetch permissions immediately
    if (userData && !userData.issuperadmin) {
      try {
        let perms = [];
        if (userData.issubadmin) {
          perms = await getSubadminPermissions();
          localStorage.setItem('subadminPermissions', JSON.stringify(perms));
        } else if (userData.isagent === 'verified' || userData.isagent === true) {
          perms = await getAgentPermissions(userData.id);
          localStorage.setItem('agentPermissions', JSON.stringify(perms));
        }
        setPermissions(perms);
      } catch (err) {
        console.error('Login permissions error:', err);
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('agentPermissions');
    localStorage.removeItem('subadminPermissions');
    setToken(null);
    setUser(null);
    setPermissions([]);
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, login, logout,
      loading, permissions,
      isCollapsed, setIsCollapsed,
      globalSearchQuery, setGlobalSearchQuery,
      showGlobalSearch, setShowGlobalSearch
    }}>
      {children}
    </AuthContext.Provider>
  );
};
